import { useEffect } from "react";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { WifiOff } from "lucide-react";
import { errorMessage } from "@/shared/i18n";
import { useOnline } from "@/shared/lib/pwa";

/** A page that couldn't load. Without a connection (the installed app opened offline)
 * it says so, and tries again when the connection is back and every little while. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const online = useOnline();
  const network = error instanceof TypeError; // fetch and import() fail this way
  const retry = () => {
    reset();
    void router.invalidate();
  };
  useEffect(() => {
    if (!network) return;
    window.addEventListener("online", retry);
    const timer = window.setInterval(retry, 15_000);
    return () => {
      window.removeEventListener("online", retry);
      window.clearInterval(timer);
    };
  }, [network]); // eslint-disable-line react-hooks/exhaustive-deps
  const offline = network || !online;
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-center" role="alert">
      {offline ? <WifiOff className="size-9 text-ink-3" /> : <img src="/favicon.svg" alt="" className="size-9" />}
      <h1 className="text-[18px] font-semibold">{offline ? t("shell.offlineTitle") : t("shell.errorTitle")}</h1>
      <p className="max-w-sm text-ink-2">{offline ? t("shell.offlineBody") : errorMessage(error)}</p>
      <button type="button" className="btn-primary" onClick={retry}>{t("common.retry")}</button>
    </div>
  );
}
